import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useInsuranceSummary, useInsurances } from "../hooks/insurance";
import type { InsuranceKind } from "../lib/insurance";

type StatusFilter = "active" | "inactive" | "all";
type ValueFilter = "all" | "with_value" | "without_value";

function formatEur(cents: number | null): string {
  if (cents === null) return "—";
  return (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString("de-DE", {
    timeZone: "UTC",
  });
}

const KIND_ORDER: InsuranceKind[] = [
  "KV",
  "HAFTPFLICHT",
  "HAUSRAT",
  "BU",
  "LV",
  "AUTO",
  "RECHTSSCHUTZ",
  "REISE",
  "SONSTIGE",
];

export function InsuranceList() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<StatusFilter>("active");
  const [valueFilter, setValueFilter] = useState<ValueFilter>("all");
  const list = useInsurances(
    status === "all" ? {} : { active: status === "active" },
  );
  // Zweite (parallel) Abfrage ohne Filter, damit wir Counts fuer alle Chips kennen.
  const all = useInsurances({});
  const summary = useInsuranceSummary();

  const allCount = all.data?.insurances.length ?? 0;
  const activeCount = (all.data?.insurances ?? []).filter((i) => i.isActive).length;
  const inactiveCount = allCount - activeCount;
  const valueCount = (all.data?.insurances ?? []).filter(
    (i) => i.valueAssetClass !== null,
  ).length;
  const totalValueCents = (all.data?.insurances ?? [])
    .filter((i) => i.isActive && i.valueAssetClass && i.currentValueCents)
    .reduce((s, i) => s + (i.currentValueCents ?? 0), 0);

  if (list.isLoading) {
    return (
      <div className="fv-card p-10 text-center" style={{ color: "var(--muted)" }}>
        …
      </div>
    );
  }
  const insurancesRaw = list.data?.insurances ?? [];
  const insurances = insurancesRaw.filter((i) => {
    if (valueFilter === "with_value") return i.valueAssetClass !== null;
    if (valueFilter === "without_value") return i.valueAssetClass === null;
    return true;
  });
  const isEmpty = insurances.length === 0;

  const grouped = KIND_ORDER.map((k) => ({
    kind: k,
    items: insurances.filter((i) => i.kind === k),
  })).filter((g) => g.items.length > 0);

  const today = new Date();
  const upcomingDeadline = (deadlineIso: string | null): boolean => {
    if (!deadlineIso) return false;
    const d = new Date(`${deadlineIso}T00:00:00Z`);
    const diffDays = Math.floor((d.getTime() - today.getTime()) / 86_400_000);
    return diffDays >= 0 && diffDays <= 60;
  };

  return (
    <section className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div
            className="text-xs font-semibold uppercase tracking-wider mb-1"
            style={{ color: "var(--muted)" }}
          >
            {t("nav.group_cashflow")}
          </div>
          <h1 className="text-2xl font-bold tracking-tight">{t("insurance.list.title")}</h1>
          {summary.data && (
            <div className="text-sm mt-1" style={{ color: "var(--muted)" }}>
              {summary.data.activeCount}{" "}
              {t("insurance.active_contracts", { defaultValue: "aktive Vertraege" })}
            </div>
          )}
        </div>
        <Link
          to="/insurance/new"
          className="px-3 py-2 rounded-lg text-white text-sm font-medium"
          style={{ background: "var(--accent)" }}
        >
          + {t("insurance.list.new")}
        </Link>
      </div>

      {/* Status-Filter */}
      <div className="flex flex-wrap items-center gap-3">
        <div
          className="flex gap-1 p-1 rounded-lg"
          style={{ background: "var(--card)", border: "1px solid var(--border)" }}
        >
          <FilterChip
            active={status === "active"}
            onClick={() => setStatus("active")}
            label={`${t("insurance.list.filter.active", { defaultValue: "Aktiv" })} (${activeCount})`}
          />
          <FilterChip
            active={status === "inactive"}
            onClick={() => setStatus("inactive")}
            label={`${t("insurance.list.filter.inactive", { defaultValue: "Deaktiviert" })} (${inactiveCount})`}
          />
          <FilterChip
            active={status === "all"}
            onClick={() => setStatus("all")}
            label={`${t("insurance.list.filter.all", { defaultValue: "Alle" })} (${allCount})`}
          />
        </div>

        {valueCount > 0 && (
          <div
            className="flex gap-1 p-1 rounded-lg"
            style={{ background: "var(--card)", border: "1px solid var(--border)" }}
          >
            <FilterChip
              active={valueFilter === "all"}
              onClick={() => setValueFilter("all")}
              label={t("insurance.list.filter.all_kinds", { defaultValue: "Alle Arten" })}
            />
            <FilterChip
              active={valueFilter === "with_value"}
              onClick={() => setValueFilter("with_value")}
              label={`${t("insurance.list.filter.with_value", { defaultValue: "Werthaltend" })} (${valueCount})`}
            />
            <FilterChip
              active={valueFilter === "without_value"}
              onClick={() => setValueFilter("without_value")}
              label={t("insurance.list.filter.without_value", { defaultValue: "Reine Risiko" })}
            />
          </div>
        )}
      </div>

      {summary.data && summary.data.activeCount > 0 && (
        <div
          className={`fv-card grid grid-cols-1 ${valueCount > 0 ? "md:grid-cols-4" : "md:grid-cols-3"} divide-y md:divide-y-0 md:divide-x`}
          style={{ borderColor: "var(--border)" }}
        >
          <StatMini
            label={t("insurance.list.summary.count")}
            value={String(summary.data.activeCount)}
          />
          <StatMini
            label={t("insurance.list.summary.monthly")}
            value={formatEur(summary.data.totalMonthlyEquivalentCents)}
            emphasis
          />
          <StatMini
            label={t("insurance.list.summary.yearly")}
            value={formatEur(summary.data.totalYearlyEquivalentCents)}
          />
          {valueCount > 0 && (
            <StatMini
              label={t("insurance.list.summary.total_value", {
                defaultValue: "Vertragswerte",
              })}
              value={formatEur(totalValueCents)}
              emphasis
            />
          )}
        </div>
      )}

      {isEmpty ? (
        <div className="fv-card p-10 text-center">
          <p style={{ color: "var(--text)" }} className="mb-3">
            {t("insurance.list.empty")}
          </p>
          <Link
            to="/insurance/new"
            className="inline-block px-3 py-2 rounded-lg text-white text-sm font-medium"
            style={{ background: "var(--accent)" }}
          >
            {t("insurance.list.empty_cta")}
          </Link>
        </div>
      ) : (
        <div className="fv-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr
                className="text-left text-xs"
                style={{ background: "var(--border-soft)", color: "var(--muted)" }}
              >
                <th className="px-3 py-2 font-semibold">{t("insurance.list.column.name")}</th>
                <th className="px-3 py-2 font-semibold">{t("insurance.list.column.insurer")}</th>
                <th className="px-3 py-2 text-right font-semibold">
                  {t("insurance.list.column.premium")}
                </th>
                <th className="px-3 py-2 text-right font-semibold">
                  {t("insurance.list.column.monthly")}
                </th>
                <th className="px-3 py-2 text-right font-semibold">
                  {t("insurance.list.column.value", { defaultValue: "Wert" })}
                </th>
                <th className="px-3 py-2 font-semibold">{t("insurance.list.column.deadline")}</th>
              </tr>
            </thead>
            {grouped.map((g) => (
              <tbody key={g.kind}>
                <tr style={{ background: "var(--card-elev)" }}>
                  <td colSpan={6} className="px-3 py-2 font-semibold">
                    {t(`insurance.kind.${g.kind}`)}
                  </td>
                </tr>
                {g.items.map((ins) => (
                  <tr
                    key={ins.id}
                    className="border-b transition-colors hover:bg-[var(--row-hover)]"
                    style={{ borderColor: "var(--border-soft)" }}
                  >
                    <td className="px-3 py-2.5">
                      <Link to={`/insurance/${ins.id}`} style={{ color: "var(--accent)" }}>
                        {ins.name}
                      </Link>
                    </td>
                    <td className="px-3 py-2.5" style={{ color: "var(--muted)" }}>
                      {ins.insurer}
                    </td>
                    <td className="px-3 py-2.5 text-right num">
                      {ins.currentPremium
                        ? `${formatEur(ins.currentPremium.amountCents)} / ${t(
                            `insurance.rhythm.${ins.currentPremium.rhythm}`,
                          )}`
                        : "—"}
                    </td>
                    <td className="px-3 py-2.5 text-right num">
                      {formatEur(ins.monthlyEquivalentCents)}
                    </td>
                    <td className="px-3 py-2.5 text-right num font-semibold">
                      {ins.valueAssetClass && ins.currentValueCents !== null
                        ? formatEur(ins.currentValueCents)
                        : ins.valueAssetClass
                          ? "—"
                          : ""}
                    </td>
                    <td className="px-3 py-2.5">
                      {ins.noticeDeadlineDate ? (
                        <span
                          style={{
                            color: upcomingDeadline(ins.noticeDeadlineDate)
                              ? "var(--warn)"
                              : "var(--muted)",
                            fontWeight: upcomingDeadline(ins.noticeDeadlineDate) ? 500 : 400,
                          }}
                        >
                          {formatDate(ins.noticeDeadlineDate)}
                        </span>
                      ) : (
                        <span style={{ color: "var(--subtle)" }}>—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            ))}
          </table>
        </div>
      )}
    </section>
  );
}

function FilterChip({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
      style={
        active
          ? { background: "var(--text)", color: "var(--bg)" }
          : { color: "var(--muted)", background: "transparent" }
      }
    >
      {label}
    </button>
  );
}

function StatMini({
  label,
  value,
  emphasis,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  return (
    <div className="p-4">
      <div className="fv-kpi-label mb-1">{label}</div>
      <div
        className={`${emphasis ? "text-2xl" : "text-xl"} font-bold tracking-tight num`}
      >
        {value}
      </div>
    </div>
  );
}
